import React, { useState } from 'react';
import { PODIUM_COLOURS, PODIUM_BG, computeRanks } from '../utils/ranking';

const styles = {
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    borderBottom: '1px solid #333',
    color: '#999',
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #222',
  },
  rank: {
    color: '#f9b000',
    fontWeight: 700,
    width: 36,
  },
};


function BreakdownRow({ breakdown, colSpan }) {
  const hits = Object.entries(breakdown)
    .map(([key, pts]) => ({ pos: parseInt(key.replace('p', ''), 10), pts }))
    .sort((a, b) => a.pos - b.pos);

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: '0 12px 10px 12px', borderBottom: '1px solid #222' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 4 }}>
          {hits.map(({ pos, pts }) => {
            const hit = pts > 0;
            return (
              <span
                key={pos}
                title={hit ? `P${pos}: +${pts} pts` : `P${pos}: miss`}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 7px',
                  borderRadius: 3,
                  background: hit ? '#14532d' : '#1a1a1a',
                  color: hit ? '#86efac' : '#555',
                  border: `1px solid ${hit ? '#166534' : '#333'}`,
                }}
              >
                P{pos}{hit ? ` +${pts}` : ''}
              </span>
            );
          })}
        </div>
      </td>
    </tr>
  );
}

export default function ScoreTable({ rows, showRaces, currentUser }) {
  const [expanded, setExpanded] = useState(null);

  if (!rows || rows.length === 0) {
    return <p style={{ color: '#666', marginTop: 16 }}>No data yet.</p>;
  }

  const hasBreakdown = rows.some(r => r.breakdown);
  const colSpan = 2 + (showRaces ? 1 : 0) + (hasBreakdown ? 1 : 0);

  // Compute true ranks respecting ties (same score = same rank)
  const scores = rows.map(r => r.score ?? r.total_score ?? 0);
  const ranks = computeRanks(rows.map((r, i) => ({ score: scores[i] })));

  return (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>#</th>
          <th style={styles.th}>Name</th>
          <th style={{ ...styles.th, textAlign: 'right' }}>Score</th>
          {showRaces && <th style={{ ...styles.th, textAlign: 'right' }}>Races</th>}
          {hasBreakdown && <th style={{ ...styles.th, width: 32 }} />}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const isExpanded = expanded === row.user_name;
          const isMe = currentUser && row.user_name === currentUser;
          const rank = ranks[i];
          const podiumIdx = rank <= 3 ? rank - 1 : null;
          const rankColour = podiumIdx !== null ? PODIUM_COLOURS[podiumIdx] : '#666';
          const podiumBg = podiumIdx !== null ? PODIUM_BG[podiumIdx] : 'transparent';
          return (
            <React.Fragment key={row.user_name}>
              <tr
                onClick={row.breakdown ? () => setExpanded(isExpanded ? null : row.user_name) : undefined}
                style={{ cursor: row.breakdown ? 'pointer' : 'default', background: isMe ? '#1c1a0e' : podiumBg }}
              >
                <td style={{ ...styles.td, ...styles.rank, color: rankColour }}>{rank}</td>
                <td style={{ ...styles.td, fontWeight: isMe ? 700 : 400, color: isMe ? '#f9b000' : 'inherit' }}>
                  {row.user_name}{isMe && ' ★'}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>
                  {scores[i]}
                </td>
                {showRaces && (
                  <td style={{ ...styles.td, textAlign: 'right', color: '#999' }}>
                    {row.races_scored}
                  </td>
                )}
                {hasBreakdown && (
                  <td style={{ ...styles.td, textAlign: 'right', color: '#555', fontSize: 12 }}>
                    {row.breakdown ? (isExpanded ? '▲' : '▼') : ''}
                  </td>
                )}
              </tr>
              {isExpanded && row.breakdown && (
                <BreakdownRow breakdown={row.breakdown} colSpan={colSpan} />
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
